// ════════════════════════════════════════════════════════════════
// Карточка со стихотворением — картинкой, чтобы сохранить или показать.
//
// Рисуем на канвасе, без внешних библиотек: подключать тяжёлый рендерер
// ради одной картинки не стоит, а канвас есть везде.
//
// Та же машинка потом пригодится для месячной карточки — формат и стиль
// заложены общими.
// ════════════════════════════════════════════════════════════════

const W = 1080;              // ширина под сторис и ленту
const PAD = 96;              // поля
const BG = '#0B0F12';        // фон приложения
const EMERALD = '#10E58E';
const INK = '#F5F7FA';
const INK_DIM = '#5A6573';

/** Переносит длинную строку по ширине, сохраняя авторские переносы стиха */
function wrap(ctx: CanvasRenderingContext2D, line: string, maxWidth: number): string[] {
  if (ctx.measureText(line).width <= maxWidth) return [line];
  const words = line.split(' ');
  const out: string[] = [];
  let cur = '';
  for (const w of words) {
    const probe = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(probe).width > maxWidth && cur) {
      out.push(cur);
      cur = w;
    } else {
      cur = probe;
    }
  }
  if (cur) out.push(cur);
  return out;
}

export async function renderPoemCard(
  poem: string,
  opts: { name?: string | null } = {},
): Promise<Blob> {
  // Без этого канвас нарисует запасным шрифтом: он не ждёт загрузки веб-шрифтов
  if (document.fonts?.ready) await document.fonts.ready;

  // Шрифт с засечками: стих в гротеске читается как интерфейсный текст.
  // Georgia есть и на iOS, и на Android, в конце — системный serif.
  const POEM_FONT = '400 41px Georgia, "Times New Roman", serif';
  const LINE_H = 72;
  const STANZA_GAP = 40;

  // Первый проход — считаем высоту, чтобы карточка была по размеру стиха,
  // а не с пустотой снизу
  const probe = document.createElement('canvas').getContext('2d')!;
  probe.font = POEM_FONT;
  const maxTextW = W - PAD * 2;

  const blocks = poem.trim().split(/\n\s*\n/).map((b) =>
    b.split('\n').flatMap((l) => wrap(probe, l.trim(), maxTextW)),
  );

  const poemH =
    blocks.reduce((s, b) => s + b.length * LINE_H, 0) + (blocks.length - 1) * STANZA_GAP;

  const HEAD = 190;   // шапка
  const FOOT = 230;   // подпись со знаком
  const H = Math.max(1350, HEAD + poemH + FOOT);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // ── Фон: тот же приглушённый градиент, что в приложении ──
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  // Цвет приглушён втрое: было «просто зелёная картинка». Отсвет должен
  // читаться как свет за кадром, а не как заливка.
  const g1 = ctx.createRadialGradient(W * 0.2, H * 0.1, 0, W * 0.2, H * 0.1, W * 1.2);
  g1.addColorStop(0, 'rgba(16,229,142,0.055)');
  g1.addColorStop(1, 'transparent');
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, W, H);

  const g2 = ctx.createRadialGradient(W * 0.85, H * 0.95, 0, W * 0.85, H * 0.95, W);
  g2.addColorStop(0, 'rgba(61,216,255,0.04)');
  g2.addColorStop(1, 'transparent');
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, W, H);

  // Виньетка — глубина вместо плоской заливки
  const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 0.75);
  vig.addColorStop(0, 'transparent');
  vig.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  // Двойная рамка: тонкая снаружи, волосяная внутри — так делают на
  // книжных титулах, и это сразу уводит от «картинки из приложения»
  ctx.strokeStyle = 'rgba(255,255,255,0.13)';
  ctx.lineWidth = 2;
  ctx.strokeRect(44, 44, W - 88, H - 88);
  ctx.strokeStyle = 'rgba(16,229,142,0.22)';
  ctx.lineWidth = 1;
  ctx.strokeRect(58, 58, W - 116, H - 116);

  // ── Шапка ──
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(16,229,142,0.75)';
  ctx.font = '500 24px Inter, system-ui, sans-serif';
  ctx.letterSpacing = '7px';
  ctx.fillText('ПУТЬ СО СЛАВОЙ · 21 ДЕНЬ', W / 2, 132);
  ctx.letterSpacing = '0px';

  // Наборный орнамент под заголовком — воздух и точка входа в текст
  const oy = 172;
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 150, oy);
  ctx.lineTo(W / 2 - 26, oy);
  ctx.moveTo(W / 2 + 26, oy);
  ctx.lineTo(W / 2 + 150, oy);
  ctx.stroke();
  ctx.fillStyle = 'rgba(16,229,142,0.5)';
  ctx.beginPath();
  ctx.arc(W / 2, oy, 3.5, 0, Math.PI * 2);
  ctx.fill();

  // ── Стихотворение ──
  ctx.fillStyle = INK;
  ctx.font = POEM_FONT;
  let y = HEAD + 40;
  for (const block of blocks) {
    for (const line of block) {
      ctx.fillText(line, W / 2, y);
      y += LINE_H;
    }
    y += STANZA_GAP;
  }

  // ── Подпись ──
  const footY = H - 150;

  // Монограмма SH в круге — знак без зависимости от файла
  const cx = W / 2;
  ctx.beginPath();
  ctx.arc(cx, footY, 40, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(16,229,142,0.12)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(16,229,142,0.55)';
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.fillStyle = EMERALD;
  ctx.font = '600 30px "Space Grotesk", Inter, sans-serif';
  ctx.fillText('SH', cx, footY + 11);

  ctx.fillStyle = INK_DIM;
  ctx.font = '400 24px Inter, system-ui, sans-serif';
  const who = opts.name ? `${opts.name} · Slava Health` : 'Slava Health';
  ctx.fillText(who, cx, footY + 86);

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas пуст'))), 'image/png', 0.95),
  );
}

/**
 * Отдаёт картинку человеку. Порядок попыток важен: в мини-аппе Телеграма
 * обычное скачивание работает не всегда, а системное «Поделиться» —
 * почти везде. Если не вышло ни то ни другое, картинку всё равно видно
 * на экране и её можно сохранить долгим нажатием.
 */
export async function sharePoemCard(blob: Blob, fileName = 'moy-put.png'): Promise<'share' | 'download'> {
  const file = new File([blob], fileName, { type: 'image/png' });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'share';
    } catch {
      // отмена пользователем — не ошибка, просто уходим в скачивание
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'download';
}
